import { useGetEvent } from '@sunrise-ui/api-client';
import {
  isNative,
  useCheckoutModalStore,
  useNotificationsModalStore,
  usePushNotifications,
} from '@sunrise-ui/primitives';
import * as WebBrowser from 'expo-web-browser';
import { useState } from 'react';

type UseTicketPurchaseOptions = {
  eventId: string | undefined;
  onSuccess?: () => void | Promise<void>;
};

/**
 * Hook to handle ticket purchase flow with authentication, notification checks,
 * and external/internal ticketing support.
 */
export function useTicketPurchase({
  eventId,
  onSuccess,
}: UseTicketPurchaseOptions) {
  const { permission } = usePushNotifications();
  const { openCheckoutModal } = useCheckoutModalStore();
  const { openNotificationsModal } = useNotificationsModalStore();
  const [isPurchasing, setIsPurchasing] = useState(false);

  const { data: event, isLoading: isLoadingEvent } = useGetEvent(eventId);

  const performPurchase = async () => {
    if (!event) return;

    // Path 1: External ticketing - open URL
    if (event.ticketing_platform_url) {
      await WebBrowser.openBrowserAsync(event.ticketing_platform_url, {
        windowFeatures: { popup: false },
      });
      await onSuccess?.();
      return;
    }

    // Path 2: Internal ticketing - show checkout modal
    if (event.id) {
      openCheckoutModal({
        eventId: event.id,
        onSuccess: async () => {
          await onSuccess?.();
        },
      });
    }
  };

  const executePurchase = async () => {
    if (!event) return;

    setIsPurchasing(true);

    try {
      // Check notification permissions on native
      if (isNative && !permission?.granted) {
        openNotificationsModal(async () => {
          await performPurchase();
        });
        return;
      }

      // Proceed with purchase (checkout handles auth if needed)
      await performPurchase();
    } finally {
      setIsPurchasing(false);
    }
  };

  return {
    executePurchase,
    isPurchasing,
    event,
    isLoadingPurchase: isLoadingEvent,
  };
}
